/**
 * Analytics tracking hook for the frontend
 * Tracks page views, searches, calculator usage, etc.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Track an analytics event
 * @param {string} eventType - Type of event (page_view, search, calculator_use, etc.)
 * @param {string} page - Page where event occurred
 * @param {object} details - Additional event details
 */
export const trackEvent = async (eventType, page = null, details = null) => {
  try {
    const payload = {
      event_type: eventType,
      page: page || window.location.pathname,
      details: details,
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
    };

    // Use sendBeacon for better reliability on page unload
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/analytics/track`, blob);
    } else {
      // Fallback to fetch
      fetch(`${API}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {}); // Silently fail
    }
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.debug('Analytics tracking error:', error);
  }
};

/**
 * Track page view
 * @param {string} pageName - Name of the page
 */
export const trackPageView = (pageName) => {
  trackEvent('page_view', pageName);
};

/**
 * Track property search
 * @param {object} searchParams - Search parameters
 */
export const trackSearch = (searchParams) => {
  trackEvent('search', '/propiedades', {
    zone: searchParams.zone,
    property_type: searchParams.property_type,
    operation: searchParams.operation,
    rooms: searchParams.rooms,
    min_price: searchParams.min_price,
    max_price: searchParams.max_price,
  });
};

/**
 * Track calculator usage
 * @param {object} calcParams - Calculator parameters
 */
export const trackCalculatorUse = (calcParams) => {
  trackEvent('calculator_use', '/calculadora', {
    property_type: calcParams.property_type,
    zone: calcParams.zone,
    area: calcParams.area,
    estimated_price: calcParams.estimated_price,
  });
};

/**
 * Track adding property to favorites
 * @param {object} property - Property details
 */
export const trackFavoriteAdd = (property) => {
  trackEvent('favorite_add', '/propiedades', {
    property_id: property.id,
    zone: property.zone,
    price: property.price,
  });
};

/**
 * Track scheduling a visit
 * @param {object} property - Property details
 */
export const trackScheduleVisit = (property) => {
  trackEvent('schedule_visit', '/propiedades', {
    property_id: property.id,
    zone: property.zone,
    price: property.price,
  });
};

/**
 * Track contact click (WhatsApp, email, phone)
 * @param {string} contactType - Type of contact (whatsapp, email, phone)
 * @param {string} page - Page where click occurred
 */
export const trackContactClick = (contactType, page = null) => {
  trackEvent('contact_click', page, {
    contact_type: contactType,
  });
};

/**
 * Track article view
 * @param {object} article - Article details
 */
export const trackArticleView = (article) => {
  trackEvent('article_view', `/noticias/${article.id}`, {
    article_id: article.id,
    title: article.title,
  });
};

/**
 * Track neighborhood guide view
 * @param {string} neighborhood - Neighborhood name
 */
export const trackNeighborhoodView = (neighborhood) => {
  trackEvent('neighborhood_view', '/barrios', {
    neighborhood: neighborhood,
  });
};

export default {
  trackEvent,
  trackPageView,
  trackSearch,
  trackCalculatorUse,
  trackFavoriteAdd,
  trackScheduleVisit,
  trackContactClick,
  trackArticleView,
  trackNeighborhoodView,
};
